import React, { ReactElement, FC, useEffect } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';
import { ScheduledEventForm } from '../models/ScheduledEventDto';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { LocalizedDatePicker } from '@thingslog/ui-components';
import { EventConfig } from './EventConfig';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';

const ScheduledEventFormScheduledRelayFields: FC<ScheduledEventFormScheduledRelayFieldsProps> = ({
  form,
}: ScheduledEventFormScheduledRelayFieldsProps) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = form;

  const { t } = useTranslation();

  const eventType = watch('event.@type');
  const fromDate = watch('fromDate');

  useEffect(() => {
    if (fromDate) {
      const computedToDate = addDays(new Date(fromDate), 1);
      setValue('toDate', computedToDate, { shouldValidate: true, shouldDirty: true });
    }
  }, [fromDate, setValue]);

  return (
    <>
      {/* From Date */}
      <div className="col-span-4 md:col-span-2">
        <Controller
          name="fromDate"
          control={control}
          rules={{ required: true }}
          render={(props: {
            field: ControllerRenderProps<ScheduledEventForm, 'fromDate'>;
            fieldState: ControllerFieldState;
            formState: UseFormStateReturn<ScheduledEventForm>;
          }): ReactElement => {
            const toDate = watch('toDate');
            return (
              <>
                <LocalizedDatePicker
                  label={t('task_scheduler_form_from_date')}
                  {...props.field}
                  inputFormat="dd/MM/yyyy"
                  renderInputTextField={{
                    size: 'small',
                    style: { backgroundColor: 'white', width: '100%' },
                  }}
                />
                {errors.fromDate && (
                  <div className="pl-4 text-sm font-normal text-red-600">
                    {errors.fromDate.message}
                  </div>
                )}
              </>
            );
          }}
        ></Controller>
      </div>

      {/* Execution Hour */}
      <div className="col-span-4 md:col-span-1">
        <FormControl fullWidth size="small" error={!!errors.executionHour}>
          <InputLabel id="execution-hour-label">
            {t('task_scheduler_form_execution_hour')}
          </InputLabel>
          <Controller
            name="executionHour"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'executionHour'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId="execution-hour-label"
                label={t('task_scheduler_form_execution_hour')}
              >
                {Array.from({ length: 24 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.executionHour && <FormHelperText>{errors.executionHour.message}</FormHelperText>}
        </FormControl>
      </div>

      {/* Execution Minute */}
      <div className="col-span-4 md:col-span-1">
        <FormControl fullWidth size="small" error={!!errors.executionMinute}>
          <InputLabel id="execution-minute-label">
            {t('task_scheduler_form_execution_minute')}
          </InputLabel>
          <Controller
            name="executionMinute"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'executionMinute'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId="execution-minute-label"
                label={t('task_scheduler_form_execution_minute')}
              >
                {Array.from({ length: 60 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.executionMinute && (
            <FormHelperText>{errors.executionMinute.message}</FormHelperText>
          )}
        </FormControl>
      </div>

      {/* Command Fields */}
      <div className="col-span-4">
        <EventConfig selectedEventType={eventType} form={form} />
      </div>
    </>
  );
};

interface ScheduledEventFormScheduledRelayFieldsProps {
  form: UseFormReturn<ScheduledEventForm, unknown, undefined>;
}

export default ScheduledEventFormScheduledRelayFields;
